/**
 * JSON-LD Schema Generators for SEO
 * Generate structured data for search engines and AI crawlers
 */

const BASE_URL = 'https://1crew3.com';

/**
 * Organization Schema - used on every page
 */
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '1crew3',
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  description: 'Yacht crew recruitment platform connecting crew with captains and owners worldwide.',
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    url: `${BASE_URL}/contact`
  }
});

/**
 * WebSite Schema with SearchAction - used on homepage
 */
export const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '1crew3',
  url: BASE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${BASE_URL}/jobs?q={search_term_string}`,
    'query-input': 'required name=search_term_string'
  }
});

/**
 * JobPosting Schema - used on individual job pages
 * @param {Object} job - Job posting data
 */
export const generateJobPostingSchema = (job) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title || job.position,
    description: job.description || `${job.position} position on yacht`,
    datePosted: job.createdAt || job.created_at || new Date().toISOString(),
    employmentType: job.employmentType || 'FULL_TIME',
    hiringOrganization: {
      '@type': 'Organization',
      name: job.yachtName || job.yacht_name || job.ownerName || '1crew3',
      sameAs: BASE_URL
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressRegion: job.region || job.location || 'Mediterranean',
        addressCountry: job.country || ''
      }
    },
    industry: 'Maritime / Yachting',
    occupationalCategory: job.position || job.category
  };

  // Add expiry date if available
  if (job.validThrough || job.expiresAt) {
    schema.validThrough = job.validThrough || job.expiresAt;
  }

  // Add salary info if available
  if (job.salaryMin || job.salaryMax || job.salary) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: job.currency || 'EUR',
      value: {
        '@type': 'QuantitativeValue',
        minValue: job.salaryMin || job.salary || 0,
        maxValue: job.salaryMax || job.salary || 0,
        unitText: 'MONTH'
      }
    };
  }

  return schema;
};

/**
 * Person Schema - used on crew profile pages
 * @param {Object} crew - Crew member data
 */
export const generatePersonSchema = (crew) => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: crew.name || `${crew.firstName} ${crew.lastName}`,
  jobTitle: crew.position || crew.desiredPosition || 'Yacht Crew',
  description: crew.bio || crew.summary || `Experienced yacht crew member seeking ${crew.position || 'yacht crew'} positions.`,
  url: `${BASE_URL}/crew/${crew.slug || crew.id}`,
  knowsAbout: [
    ...(crew.certifications || []),
    ...(crew.skills || [])
  ].filter(Boolean),
  workLocation: {
    '@type': 'Place',
    name: crew.preferredRegion || crew.location || 'Worldwide'
  }
});

/**
 * BreadcrumbList Schema - used on all pages
 * @param {Array} items - Array of { name, url } objects
 */
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
  }))
});

/**
 * FAQPage Schema - used for FAQ sections
 * @param {Array} faqs - Array of { question, answer } objects
 */
export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

/**
 * Article Schema - used on blog posts
 * @param {Object} article - Article data
 */
export const generateArticleSchema = (article) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description || article.excerpt,
  author: {
    '@type': 'Organization',
    name: '1crew3'
  },
  datePublished: article.datePublished || article.createdAt || new Date().toISOString(),
  dateModified: article.dateModified || article.updatedAt || new Date().toISOString(),
  publisher: {
    '@type': 'Organization',
    name: '1crew3',
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo.png`
    }
  },
  mainEntityOfPage: article.url || `${BASE_URL}/blog/${article.slug}`,
  image: article.image || `${BASE_URL}/og-image.png`
});

/**
 * Combine multiple schemas into an array
 * @param {...Object} schemas - Schema objects to combine
 */
export const combineSchemas = (...schemas) => schemas.filter(Boolean);
